import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  listArticles, 
  getArticleById,
  createArticle, 
  updateArticle, 
  deleteArticle,
  updateArticleOrders
} from '../../api/article';
import { message } from 'antd';

// 分页查询资讯文章列表
export const fetchArticles = createAsyncThunk(
  'article/fetchList',
  async ({ page = 1, size = 10, queryDTO = {} }, { rejectWithValue }) => {
    try {
      const response = await listArticles(page, size, queryDTO);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取文章列表失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 根据ID获取资讯文章详情
export const fetchArticleById = createAsyncThunk(
  'article/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getArticleById(id);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取文章详情失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 创建资讯文章
export const createArticleAsync = createAsyncThunk(
  'article/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await createArticle(data);
      message.success('创建成功');
      // 创建成功后重新获取列表
      dispatch(fetchArticles({ page: 1, size: 10 }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '创建失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 更新资讯文章
export const updateArticleAsync = createAsyncThunk(
  'article/update',
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await updateArticle(id, data);
      message.success('更新成功');
      // 更新成功后重新获取列表
      dispatch(fetchArticles({ page: 1, size: 10 }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 删除资讯文章
export const deleteArticleAsync = createAsyncThunk(
  'article/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await deleteArticle(id);
      message.success('删除成功');
      // 删除成功后重新获取列表
      dispatch(fetchArticles({ page: 1, size: 10 }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '删除失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 批量更新文章排序
export const updateArticleOrdersAsync = createAsyncThunk(
  'article/updateOrders',
  async (items, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updateArticleOrders(items);
      message.success('排序更新成功');
      // 排序更新成功后重新获取当前页的列表
      const state = getState();
      const { currentPage, pageSize, queryDTO } = state.article;
      // 使用保存的查询参数重新获取列表
      dispatch(fetchArticles({ 
        page: currentPage, 
        size: pageSize, 
        queryDTO: queryDTO || {} 
      }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '排序更新失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const articleSlice = createSlice({
  name: 'article',
  initialState: {
    articles: [],
    total: 0,
    currentPage: 1,
    pageSize: 10,
    loading: false,
    currentArticle: null,
    queryDTO: {},
    error: null
  },
  reducers: {
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setCurrentArticle: (state, action) => {
      state.currentArticle = action.payload;
    },
    clearCurrentArticle: (state) => {
      state.currentArticle = null;
    }
  },
  extraReducers: (builder) => {
    // 查询列表
    builder.addCase(fetchArticles.pending, (state, action) => {
      state.loading = true;
      // 保存查询参数
      if (action.meta?.arg?.queryDTO) {
        state.queryDTO = action.meta.arg.queryDTO;
      }
    });
    builder.addCase(fetchArticles.fulfilled, (state, action) => {
      state.loading = false;
      state.articles = action.payload.records || [];
      state.total = action.payload.total || 0;
      state.currentPage = action.payload.current || action.payload.page || 1;
      state.pageSize = action.payload.size || 10;
    });
    builder.addCase(fetchArticles.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取详情
    builder.addCase(fetchArticleById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchArticleById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentArticle = action.payload;
    });
    builder.addCase(fetchArticleById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 创建
    builder.addCase(createArticleAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createArticleAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createArticleAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 更新
    builder.addCase(updateArticleAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateArticleAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateArticleAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 删除
    builder.addCase(deleteArticleAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteArticleAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteArticleAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 批量更新排序
    builder.addCase(updateArticleOrdersAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateArticleOrdersAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateArticleOrdersAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  }
});

export const { setPage, setPageSize, setCurrentArticle, clearCurrentArticle } = articleSlice.actions;
export default articleSlice.reducer;

